// Types, adapters, and state derivation for the Insurance Eligibility Check slideout (OLIO-2301).
//
// The referral-intake workflow runs two integration-backed auto-checks: PayerSearch
// (`payer_name` → candidate list → a human-picked `external_payer_id`) and BenefitsEligibility
// (`external_payer_id` + subscriber + provider → `benefits_eligibility_result`, a Stedi 270/271).
// This module is the single place that reads those workflow values and reduces them to the
// state the UI renders. It renders the rich detail off the *raw* Stedi 271 carried in
// `benefits_eligibility_result.checks[0].stediResponse` — kept isolated here so it can be
// swapped for a cleaner backend-provided shape later without touching the views.
//
// Value NAMES are snake_case and read verbatim (olio-api leaves them alone); the value
// CONTENT is camelCased by olio-api's OliveBranch, so every field below is camelCase.

import { startOfMonth } from 'date-fns';

import { parseDate } from 'lib/date';
import Workflow, { ActionStatus, WorkflowAction } from 'models/Workflow';
import type { ManualBenefitsPayer } from 'services/api/workflows';

import { MANUAL_BENEFITS_MODE, PRIVATE_PAY_NAME, VALUE_NAMES } from '../constants';

// ---- Workflow value names + action kinds ----
export const PAYER_CANDIDATES_VALUE = 'payer_candidates';
export const BENEFITS_ELIGIBILITY_RESULT_VALUE = 'benefits_eligibility_result';
// The eligibility service's own id for the payer a person picked. Prefixed because `payer_id`
// already names a foreign key to a payer Group on the api side (OLIO-3326).
export const EXTERNAL_PAYER_ID_VALUE = 'external_payer_id';
// Committed alongside it when a payer is picked (PayerSearch produces it) so the result
// Overview can show the payer logo — the payer_candidates are gone by result time.
export const PAYER_LOGO_VALUE = 'payer_logo';
// The picked payer's eligibility-support flag, which a Payer Search pick and a manual directory pick both
// write on the coverage.
export const PAYER_ELIGIBILITY_SUPPORT_VALUE = 'payer_eligibility_support';

export const PAYER_SEARCH_KIND = 'PayerSearch';
export const BENEFITS_ELIGIBILITY_KIND = 'BenefitsEligibility';

// Stedi eligibility-transaction support (payer directory `transactionSupport.eligibilityCheck`).
export const ELIGIBILITY_SUPPORTED = 'SUPPORTED';

// ---- Payer candidate (the `payer_candidates` value on the PayerSearch action) ----
export interface PayerCandidate {
  primaryPayerId: string | null;
  stediId: string | null;
  displayName: string | null;
  names: string[];
  aliases: string[];
  // SUPPORTED | NOT_SUPPORTED | ENROLLMENT_REQUIRED (Stedi transaction support).
  eligibilitySupported: string | null;
  coverageTypes: string[];
  operatingStates: string[];
  programs: string[];
  logo: string | null;
}

// ---- Skilled-nursing summary (carried on summary.skilledNursing; decoded by the backend) ----
export interface SnfBucket {
  status?: string;
  coinsurancePercent?: number | null;
  copay?: number | null;
  deductible?: number | null;
  outOfPocket?: number | null;
  per?: string | null;
  dayLimit?: { value: number | null; unit: string | null; period: string | null; source: string };
  priorAuth?: { required: string; source: string };
  notes?: string[];
}

export interface SkilledNursingSummary {
  skilledNursingReported?: boolean;
  reason?: string;
  inNetwork?: SnfBucket;
  outNetwork?: SnfBucket;
  serviceTypeCodesSeen?: string[];
}

export interface BenefitsEligibilitySummary {
  activeCoverage?: string; // active | inactive | mixed | unknown
  coverageDates?: { start: string | null; end: string | null } | null;
  skilledNursing?: SkilledNursingSummary;
}

// ---- Raw Stedi 271 slice we render (only the fields the designed screens use) ----
export interface Stedi271Subscriber {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  dateOfBirth?: string; // YYYYMMDD
  gender?: string;
  memberId?: string;
  groupNumber?: string;
  groupDescription?: string;
  planNumber?: string;
  address?: { address1?: string; address2?: string; city?: string; state?: string; postalCode?: string };
}

export interface Stedi271Payer {
  name?: string;
  // Stedi returns the payer id as `payorIdentification` (payOR); older shapes used
  // `payerIdentification`. Read both.
  payorIdentification?: string;
  payerIdentification?: string;
  entityIdentifier?: string;
}

export interface BenefitLine {
  code?: string; // benefit type code: 1-5 active, 6-8 inactive, B copay, C deductible, G out of pocket, …
  name?: string; // "Active Coverage", "Co-Payment", "Deductible", …
  serviceTypeCodes?: string[];
  serviceTypes?: string[];
  coverageLevelCode?: string;
  coverageLevel?: string; // Individual | Family | Employee Only
  insuranceTypeCode?: string;
  insuranceType?: string;
  timeQualifierCode?: string;
  timeQualifier?: string; // Calendar Year | Visit | …
  benefitAmount?: string;
  benefitPercent?: string; // decimal string, e.g. "0.3" = 30%
  benefitQuantity?: string;
  quantityQualifier?: string;
  inPlanNetworkIndicatorCode?: string;
  inPlanNetworkIndicator?: string; // Yes | No | Not Applicable
  benefitDescription?: string;
  additionalInformation?: { description?: string }[];
  planCoverage?: string;
  // Service limit — the period a benefit applies over (e.g. a copay "per 34 days"). Stedi carries
  // this separately from timeQualifier (which the Coverage column uses), so it needs its own render.
  benefitsServiceDelivery?: { timePeriodQualifierCode?: string; timePeriodQualifier?: string; numOfPeriods?: string }[];
  authOrCertIndicator?: string; // Y = prior auth required, N = not required
}

export interface Stedi271 {
  subscriber?: Stedi271Subscriber;
  planInformation?: Record<string, string>;
  planDateInformation?: Record<string, string>;
  payer?: Stedi271Payer;
  planStatus?: { statusCode?: string; status?: string; serviceTypeCodes?: string[] }[];
  benefitsInformation?: BenefitLine[];
  errors?: { code?: string; description?: string; followupAction?: string }[];
}

export interface EligibilityCheck {
  serviceTypeCodes?: string[];
  statusCode?: number | null;
  eligibilityCheckId?: string | null;
  stediResponse?: Stedi271;
}

export interface EligibilityResult {
  summary?: BenefitsEligibilitySummary;
  classification?: string; // success | recoverable_error | payer_unavailable | fatal_error
  checks?: EligibilityCheck[];
  // OLIO-2609: durable marker for the downloadable eligibility PDF. `eligibilityCheckId` present =
  // available (download via GET /workflows/:id/eligibility_pdf); `error` present = unavailable.
  pdf?: { eligibilityCheckId?: string; filename?: string; error?: string };
}

// AAA codes that mean the payer isn't set up for this provider/eligibility (→ "not enrolled"),
// vs. transient/other failures (→ generic error). Recoverable input codes (72/73/75) surface as
// classification === 'recoverable_error' and drive the "no results found" state instead.
const PAYER_ENROLLMENT_AAA = new Set(['41', '43', '79']);

// ---- A person's own record of the coverage's benefits, entered when the check could not answer ----
export interface ManualBenefitsAttachment {
  id: string;
  filename: string;
  contentType?: string | null;
}

export interface ManualBenefitsRecord {
  source: 'manual';
  payer: ManualBenefitsPayer;
  memberId: string | null;
  note: string | null;
  attachments: ManualBenefitsAttachment[];
}

// ---- The states the slideout renders ----
export type EligibilityState =
  | 'select-payer'
  | 'manual'
  | 'running'
  | 'active-coverage'
  | 'no-active-coverage'
  | 'payer-not-enrolled'
  | 'no-results'
  // The search ran and matched nothing, so there is no candidate to pick and the payer name itself
  // is what needs fixing. Distinct from 'no-results', which is the ELIGIBILITY check coming back
  // empty for a payer that was chosen (OLIO-3426).
  | 'no-payer-match'
  | 'error'
  | 'canceled'
  | 'blocked'
  | 'pending';

export interface EligibilityContext {
  // The coverage this context describes (design 008 §3e). Null on a referral whose type declares
  // no coverage dimension — which is every referral until OLIO-2793 publishes.
  facetId: string | null;
  payerSearch?: WorkflowAction;
  benefits?: WorkflowAction;
  candidates: PayerCandidate[];
  result: EligibilityResult | null;
  manual: ManualBenefitsRecord | null;
  payerId: string | null;
  selectedCandidate?: PayerCandidate;
  state: EligibilityState;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map((v) => String(v)).filter(Boolean) : [];
}

// Read a workflow value's content as `unknown` — the model types `value` as `string | null`,
// but integration-completed values hold nested objects/arrays (documented simplification).
// Scoped to one coverage: every value this module reads is coverage-scoped once OLIO-2793
// publishes, so reading them unscoped would hand back a sibling coverage's answer.
function rawValue(workflow: Workflow, name: string, facetId: string | null): unknown {
  return workflow.valueFor(name, { facetId })?.value as unknown;
}

export function parsePayerCandidates(value: unknown): PayerCandidate[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((raw) => asRecord(raw))
    .filter((r): r is Record<string, unknown> => r !== null)
    .map((r) => ({
      primaryPayerId: (r.primaryPayerId as string) ?? null,
      stediId: (r.stediId as string) ?? null,
      displayName: (r.displayName as string) ?? null,
      names: asStringArray(r.names),
      aliases: asStringArray(r.aliases),
      eligibilitySupported: (r.eligibilitySupported as string) ?? null,
      coverageTypes: asStringArray(r.coverageTypes),
      operatingStates: asStringArray(r.operatingStates),
      programs: asStringArray(r.programs),
      logo: (r.logo as string) ?? null,
    }));
}

export function parseEligibilityResult(value: unknown): EligibilityResult | null {
  const r = asRecord(value);
  if (!r) return null;
  if (!('summary' in r) && !('classification' in r) && !('checks' in r)) return null;
  return r as EligibilityResult;
}

// Whether a coverage's benefits are documented by hand (OLIO-3229, Option A). Read off the coverage's
// own `benefits_mode`, never the shape of its last result: that is what the API's rules read too, so
// the panel, the restart list and the engine can't disagree about which coverages are manual.
//
// The coverage's own row only, never `valueFor`'s referral-level fallback, the same rule the API reads
// by (Workflow::BenefitsMode): a referral-level row would make every coverage manual.
export function isManualCoverage(workflow: Workflow, facetId: string | null): boolean {
  if (!facetId) return false;
  return workflow.values.some(
    (value) =>
      value.name === VALUE_NAMES.BENEFITS_MODE && value.facetId === facetId && value.value === MANUAL_BENEFITS_MODE
  );
}

// The payer a manual coverage holds, read off the coverage rather than the record: Edit Demographics
// edits the coverage's payer and member ID, so the record no longer carries them (one copy). A directory
// pick leaves the check id and support flag on the coverage, and an edit there clears them, which is how
// it reads back as a typed payer.
function coveragePayer(workflow: Workflow, facetId: string | null, recorded: ManualBenefitsPayer): ManualBenefitsPayer {
  const name = String(workflow.valueFor(VALUE_NAMES.PAYER_NAME, { facetId })?.value ?? '');
  const primaryPayerId = (workflow.valueFor(EXTERNAL_PAYER_ID_VALUE, { facetId })?.value as string) || null;
  if (primaryPayerId) {
    return {
      type: 'stedi',
      name,
      stediId: recorded.type === 'stedi' ? (recorded.stediId ?? null) : null,
      primaryPayerId,
      eligibilitySupported: (workflow.valueFor(PAYER_ELIGIBILITY_SUPPORT_VALUE, { facetId })?.value as string) ?? null,
    };
  }
  return { type: name === PRIVATE_PAY_NAME ? 'private_pay' : 'other', name, stediId: null };
}

export function parseManualBenefits(value: unknown): ManualBenefitsRecord | null {
  const r = asRecord(value);
  if (r?.source !== 'manual') return null;

  const payer = asRecord(r.payer);
  return {
    source: 'manual',
    payer: {
      type: (payer?.type as ManualBenefitsRecord['payer']['type']) ?? 'other',
      name: String(payer?.name ?? ''),
      stediId: (payer?.stediId as string) ?? null,
      // A directory pick's check id and support flag, so an edit that keeps the payer sends them back.
      ...(payer?.type === 'stedi' && {
        primaryPayerId: (payer.primaryPayerId as string) ?? null,
        eligibilitySupported: (payer.eligibilitySupported as string) ?? null,
      }),
    },
    memberId: (r.memberId as string) ?? null,
    note: (r.note as string) ?? null,
    attachments: (Array.isArray(r.attachments) ? r.attachments : [])
      .map((raw) => asRecord(raw))
      .filter((a): a is Record<string, unknown> => a !== null && Boolean(a.id))
      .map((a) => ({
        id: String(a.id),
        filename: String(a.filename ?? 'File'),
        contentType: (a.contentType as string) ?? null,
      })),
  };
}

export function isPayerSupported(candidate?: PayerCandidate): boolean {
  // Treat unknown/missing support as "try it" — only NOT_SUPPORTED / ENROLLMENT_REQUIRED block.
  return !candidate?.eligibilitySupported || candidate.eligibilitySupported === ELIGIBILITY_SUPPORTED;
}

// The payer's alternate names, shown on the candidate card + in the "View N more" modal. We use
// only Stedi's `names` (human-readable alternate names like "Ambetter"), NOT `aliases` — aliases
// are payer-ID codes ("10451", "AZHNT") that aren't meaningful to a user. Drop the displayName
// (it's the card title already) and dedupe case-insensitively while preserving order.
export function payerOtherNames(candidate: PayerCandidate): string[] {
  const primary = (candidate.displayName ?? '').trim().toLowerCase();
  const seen = new Set<string>(primary ? [primary] : []);
  const result: string[] = [];
  for (const name of candidate.names) {
    const trimmed = name.trim();
    const key = trimmed.toLowerCase();
    if (!trimmed || seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }
  return result;
}

// The primary 271 (the "30" health-plan-coverage check) — the AG probe rides along by reference.
export function primaryCheck(result: EligibilityResult | null): EligibilityCheck | undefined {
  return result?.checks?.find((c) => c.stediResponse) ?? result?.checks?.[0];
}

function resultHasPayerEnrollmentError(result: EligibilityResult | null): boolean {
  const errors = primaryCheck(result)?.stediResponse?.errors ?? [];
  return errors.some((e) => e.code != null && PAYER_ENROLLMENT_AAA.has(String(e.code)));
}

// Every coverage's context, in coverage order (OLIO-2860). A referral with no coverages yields
// exactly one context whose facetId is null — the same single-context shape this module always
// returned, which is why nothing on screen changes until coverages actually exist.
//
// WHICH coverages: derived from the eligibility actions rather than from workflow.facets, so this
// can't disagree with the work that exists — a coverage appears here precisely when it has checks
// to show.
//
// WHAT ORDER: the coverage's own `position`. The API's action sort key already ends with it, so
// first-seen order would usually agree — but leaning on that couples this to the payload's sort,
// and OLIO-2794's tab order will be built on this. A run-level action (null coverage) sorts first,
// matching the payload.
export function readEligibilities(workflow: Workflow): EligibilityContext[] {
  const facetIds = [
    ...new Set(
      workflow.actions
        .filter((a) => a.kind === PAYER_SEARCH_KIND || a.kind === BENEFITS_ELIGIBILITY_KIND)
        .map((a) => a.facetId)
    ),
  ];
  const positionOf = (facetId: string | null) =>
    facetId === null ? -1 : (workflow.facets.find((facet) => facet.id === facetId)?.position ?? Infinity);

  return (facetIds.length ? facetIds.sort((a, b) => positionOf(a) - positionOf(b)) : [null]).map((facetId) =>
    readEligibility(workflow, facetId)
  );
}

// Reduce one coverage's two auto-check actions + their values to a single UI state. Results take
// precedence over the payer-selection stage: once BenefitsEligibility has run (or failed),
// the outcome is what matters; before that we're either picking a payer or still waiting.
export function readEligibility(workflow: Workflow, facetId: string | null = null): EligibilityContext {
  // Coverage-scoped on both axes: the action has to belong to this coverage, and so does every
  // value read below. Matching on facetId also covers the no-coverage case, where every action
  // and value carries null and this behaves exactly as the unscoped version did.
  const ofThisCoverage = (a: WorkflowAction) => a.facetId === facetId;
  const payerSearch = workflow.actions.find((a) => a.kind === PAYER_SEARCH_KIND && ofThisCoverage(a));
  const benefits = workflow.actions.find((a) => a.kind === BENEFITS_ELIGIBILITY_KIND && ofThisCoverage(a));

  // Both live on the action's `outputs` while it is action_required (uncommitted evidence, not a
  // committed value). PayerSearch's candidates are only ever there; the benefits result is a
  // committed value once the check completes, but rides on the action's outputs in the
  // recoverable-error (no-results) case — so prefer the value, then fall back to the output.
  const rawCandidates =
    payerSearch?.outputFor(PAYER_CANDIDATES_VALUE) ?? rawValue(workflow, PAYER_CANDIDATES_VALUE, facetId);
  const candidates = parsePayerCandidates(rawCandidates);
  // Whether the API actually TOLD us the list, as opposed to us defaulting it. `parsePayerCandidates`
  // returns [] for an absent key, a non-array, and a genuinely empty result alike, and the index
  // payload ships no outputs at all (WorkflowAction) — so an empty `candidates` on its own cannot
  // carry the claim "the search matched nothing". Same reasoning the blocked branch gives for not
  // keying on `unobtainableValues` being non-empty, in the other direction.
  const candidatesKnown = Array.isArray(rawCandidates);
  const rawResult =
    rawValue(workflow, BENEFITS_ELIGIBILITY_RESULT_VALUE, facetId) ??
    benefits?.outputFor(BENEFITS_ELIGIBILITY_RESULT_VALUE);
  const result = parseEligibilityResult(rawResult);
  // A manual coverage's record, with the payer and member ID it now holds. Only while the instance that
  // recorded it is current and completed.
  const recorded = isManualCoverage(workflow, facetId) && benefits?.isCompleted ? parseManualBenefits(rawResult) : null;
  const manual = recorded && {
    ...recorded,
    payer: coveragePayer(workflow, facetId, recorded.payer),
    memberId: (workflow.valueFor(VALUE_NAMES.MEMBER_ID, { facetId })?.value as string) || null,
  };
  const payerId = (workflow.valueFor(EXTERNAL_PAYER_ID_VALUE, { facetId })?.value as string) ?? null;
  const selectedCandidate = payerId ? candidates.find((c) => c.primaryPayerId === payerId) : undefined;

  const state = deriveEligibilityState({
    payerSearch,
    benefits,
    candidates,
    candidatesKnown,
    result,
    manual,
    payerId,
    selectedCandidate,
  });

  return { facetId, payerSearch, benefits, candidates, result, manual, payerId, selectedCandidate, state };
}

// The verification a step-level concern hangs on: the first current Benefit Verification in coverage
// order — the primary's, where there is one. The step's note thread is read and posted through it
// (the API reads every instance's notes as one thread), and a notification link whose own instance
// has since been superseded opens it.
export function currentVerification(workflow: Workflow): WorkflowAction | undefined {
  return readEligibilities(workflow)
    .map((context) => context.benefits)
    .find((action): action is WorkflowAction => !!action);
}

// Every eligibility action across every coverage — what the collapsed checklist row and the rail
// child roll up over. With two coverages there are up to four actions behind that one row.
export function eligibilityActions(contexts: EligibilityContext[]): WorkflowAction[] {
  return contexts.flatMap((c) => [c.payerSearch, c.benefits]).filter((a): a is WorkflowAction => !!a);
}

// The status color (row icon, rail child, category pill) is no longer derived here — each eligibility
// action carries an API-derived `effectiveStatus`, and one shared fold (rollupIndicator, via
// eligibilityRowDisplay) reads it everywhere, so those surfaces can't disagree. This state is now only
// the source for the row's *summary sentence* (ELIGIBILITY_SUMMARY) and which slideout view renders —
// a single color can't tell no-active-coverage from no-results from payer-not-enrolled.

export function deriveEligibilityState(ctx: {
  payerSearch?: WorkflowAction;
  benefits?: WorkflowAction;
  candidates: PayerCandidate[];
  // Did the payload carry the candidate list at all? Defaults false, so a caller that cannot tell
  // never asserts the search matched nothing — see `no-payer-match` at the bottom.
  candidatesKnown?: boolean;
  result: EligibilityResult | null;
  manual?: ManualBenefitsRecord | null;
  payerId: string | null;
  selectedCandidate?: PayerCandidate;
}): EligibilityState {
  const { payerSearch, benefits, candidates, candidatesKnown, result, manual, selectedCandidate } = ctx;

  // A manual coverage is manual whatever else is true of it: nothing is picked or checked there.
  if (manual) return 'manual';

  // Payer selection is the gate: while PayerSearch awaits a human pick, that's the state to show —
  // even if a seeded id let BenefitsEligibility start, the check isn't meaningful until the
  // payer is confirmed. So this wins over the benefits outcomes below.
  //
  // Still gated on there being something to pick. The empty-list case is answered at the BOTTOM of
  // this function, not here — see 'no-payer-match'.
  if (payerSearch?.isActionRequired && candidates.length > 0) return 'select-payer';

  // BenefitsEligibility outcome.
  if (benefits?.isCompleted && result) {
    return result.summary?.activeCoverage === 'inactive' ? 'no-active-coverage' : 'active-coverage';
  }

  if (benefits?.status === ActionStatus.ERRORED) {
    if (resultHasPayerEnrollmentError(result) || (selectedCandidate && !isPayerSupported(selectedCandidate))) {
      return 'payer-not-enrolled';
    }
    return 'error';
  }

  // Both of the outcomes the api reports at `action_required`, and the order matches the errored
  // branch above: an enrollment reject is the more specific answer, so it wins over no-results.
  //
  // The enrollment half was missing entirely, and that is the whole of the "flagged step with no
  // slideout" QA found (OLIO-3426). OLIO-3343 moved provider-enrollment rejects off `errored` and
  // onto `action_required` so their AAA 41/43 survives — BenefitsEligibility#flagged_outcome? reads
  // `no_results?(action) || payer_enrollment_error?(action)` there — but this side only followed the
  // move for no-results. So a rejected payer flagged the badge (which folds effectiveStatus) while
  // the state fell all the way to 'pending', which says the check never started AND, because
  // 'pending' is what marks the row as still waiting, took away the click that would have shown the
  // reader otherwise.
  if (benefits?.status === ActionStatus.ACTION_REQUIRED) {
    if (resultHasPayerEnrollmentError(result)) return 'payer-not-enrolled';
    if (result?.classification === 'recoverable_error') return 'no-results';
  }

  // The run was canceled before the check produced anything (e.g. confirmed duplicate) —
  // after the result branches above, so a real outcome that did land still shows.
  if (payerSearch?.status === ActionStatus.CANCELED || benefits?.status === ActionStatus.CANCELED) return 'canceled';

  // The engine couldn't obtain an input this check needs, so it parked the step (WorkflowAction#flag!).
  // Before OLIO-3330 this fell through to 'pending' and rendered "Eligibility check not started" — the
  // reader was told nothing and offered nothing, which is exactly what made "selecting a payer does
  // nothing" so hard to explain.
  //
  // ABOVE `running`, and that ordering is the whole point rather than a preference. The two steps are
  // independent: a payer search can still be in flight while the verification is already parked on
  // something that search will never supply. Reading `running` first painted a spinner over a check
  // that cannot start, promising results that can never arrive — worse than the "not started" copy
  // this ticket was raised about. Safe in the other direction too: Workflow::Closure counts a running
  // action as a live producer, so BenefitsEligibility can never flag on a name PayerSearch produces.
  // A flag beside a running payer search therefore always means stuck on something else.
  //
  // Keyed on the FLAG ITSELF, not on whether it named any values. `unobtainableValues` defaults to []
  // whenever the payload omits it (WorkflowAction), so keying on a non-empty list would send a flagged
  // check straight back to "not started" — this ticket's bug, reachable again through nothing worse
  // than a serializer that didn't send the field. The copy degrades instead: it says the check needs
  // more information without naming fields, which is what the checklist's FlaggedPrompt already does.
  //
  // And keyed on the LIFECYCLE status, never effectiveStatus: BenefitsEligibility also PRESENTS real
  // outcomes as flagged (inactive coverage, an enrollment reject, a no-results), and those are already
  // answered by the result branches above. This is the one flag that means nothing ran.
  if (isParked(payerSearch) || isParked(benefits)) return 'blocked';

  // Either step still in flight.
  if (payerSearch?.status === ActionStatus.RUNNING || benefits?.status === ActionStatus.RUNNING) return 'running';

  // A selected payer that isn't eligibility-enrolled (pre-check signal).
  if (selectedCandidate && !isPayerSupported(selectedCandidate)) return 'payer-not-enrolled';

  // The search ran and matched nothing (OLIO-3426, QA). The platform calls back `action_required`
  // with whatever it found, an EMPTY list included, so a payer name nothing matched used to fall all
  // the way to 'pending' — which claims nothing has happened yet about a search that came back. That
  // is what made the row read "Flagged: Benefit Verification" over a sentence describing the waiting
  // state: the word folds effectiveStatus and the sentence folds outcomes, and only the word knew.
  //
  // LAST, not up beside 'select-payer', and that ordering is the point. Every branch above names
  // something more specific that is also true and also has to be fixed — most reachably `blocked`,
  // since a referral to a facility with no NPI parks the verification on provider_npi whatever the
  // payer search returns. Answering the empty list early would have hidden all of them behind this
  // one sentence. So this only ever replaces 'pending', which said nothing at all.
  // Only when the payload actually carried an empty list. An absent one reads as "we do not know"
  // and stays 'pending', because this sentence asks the coordinator to change the payer name on the
  // referral — telling them that about a search which really did return five payers invites them to
  // overwrite a correct value. Inert-and-unhelpful is the safe failure here; confidently wrong is not.
  if (payerSearch?.isActionRequired && candidatesKnown) return 'no-payer-match';

  return 'pending';
}

const isParked = (action?: WorkflowAction) => action?.status === ActionStatus.FLAGGED;

// What a blocked check is waiting on — the union across both steps, since either can be the one
// parked (a coverage with no payer name never gets as far as a payer to pick). May be EMPTY, and the
// copy has to hold up when it is; see the state branch above. Names only, the caller resolves them to
// labels through the definition's own value catalog.
export function blockedValueNames(ctx: { payerSearch?: WorkflowAction; benefits?: WorkflowAction }): string[] {
  const parked = [ctx.payerSearch, ctx.benefits].filter(isParked);

  return Array.from(new Set(parked.flatMap((action) => action?.unobtainableValues ?? [])));
}

// ---- Presentation helpers ----

// "20240401" -> "04/23/1977"-style; passthrough for anything not YYYYMMDD.
export function formatStediDate(s: string | null | undefined): string {
  if (!s) return '—';
  const m = /^(\d{4})(\d{2})(\d{2})$/.exec(s);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : s;
}

export function subscriberFullName(sub?: Stedi271Subscriber): string {
  if (!sub) return '';
  return [sub.firstName, sub.middleName, sub.lastName].filter(Boolean).join(' ');
}

// Skilled-nursing service type codes (X12): AG Skilled Nursing Care, AH SNF Room & Board,
// A2 physician visit – SNF. Pulled into their own section, so excluded from the generic table.
export const SNF_STC_CODES = ['AG', 'AH', 'A2'];

// The service types most relevant to a SNF referral — shown expanded; the rest collapse behind
// a "show all" toggle (a real 271 can carry 30+ codes).
export const PRIORITY_STC_CODES = ['30', '1'];

// X12 EB01 benefit-type codes for active (1-5) vs inactive (6-8) / non-covered (I).
const ACTIVE_BENEFIT_CODES = new Set(['1', '2', '3', '4', '5']);
const INACTIVE_BENEFIT_CODES = new Set(['6', '7', '8', 'I']);

// A benefit's "Coverage" cell: a percent, a dollar amount (with a sensible time qualifier), or a
// plain covered/not-covered verdict for a status-only line — never the redundant benefit name.
export function benefitCoverageText(line: BenefitLine): string {
  if (line.benefitPercent != null && line.benefitPercent !== '') {
    const pct = Math.round(Number(line.benefitPercent) * 100);
    return Number.isFinite(pct) ? `${pct}%` : `${line.benefitPercent}`;
  }
  if (line.benefitAmount != null && line.benefitAmount !== '') {
    const amount = `$${line.benefitAmount}`;
    const tq = line.timeQualifier;
    if (!tq) return amount;
    // "Remaining" reads better as a suffix than "per remaining"; everything else is "/ visit" etc.
    return tq.toLowerCase() === 'remaining' ? `${amount} remaining` : `${amount} / ${tq.toLowerCase()}`;
  }
  if (line.code && ACTIVE_BENEFIT_CODES.has(line.code)) return 'Covered';
  if (line.code && INACTIVE_BENEFIT_CODES.has(line.code)) return 'Not covered';
  return '—';
}

export function benefitDescriptionText(line: BenefitLine): string {
  if (line.benefitDescription) return line.benefitDescription;
  const extra = (line.additionalInformation ?? []).map((a) => a.description).filter(Boolean);
  return extra.join(' ') || '—';
}

// Service limit — the applicable period from benefitsServiceDelivery, e.g. "per 34 days". Distinct
// from the Coverage cell's timeQualifier; the 271 (and Stedi's PDF) surface both. Null when absent.
export function benefitServiceLimitText(line: BenefitLine): string | null {
  const parts = (line.benefitsServiceDelivery ?? [])
    .map(({ numOfPeriods: n, timePeriodQualifier: unit }) => {
      if (!n || !unit) return null;
      // Normalize to the singular stem first so an already-plural unit ("Days") doesn't double up,
      // then pluralize by count: "per 34 days" / "per 1 day".
      const base = unit.toLowerCase().replace(/s$/, '');
      return `per ${n} ${base}${Number(n) === 1 ? '' : 's'}`;
    })
    .filter((p): p is string => Boolean(p));
  return parts.length ? parts.join(', ') : null;
}

// Prior authorization — authOrCertIndicator: "Y" = required, "N" = not required. Null when absent.
export function benefitPriorAuthText(line: BenefitLine): string | null {
  const ind = line.authOrCertIndicator?.toUpperCase();
  if (ind === 'Y') return 'Required';
  if (ind === 'N') return 'Not required';
  return null;
}

// The plan/product name for a coverage type — the Stedi 271 `planCoverage` on a benefit line (e.g.
// STC 30 → "Open Access Plus"). OLIO-2686. Returns the first STC-30 line that carries one.
export function planCoverageName(stedi: Stedi271 | undefined): string | null {
  const line = (stedi?.benefitsInformation ?? []).find((b) => b.serviceTypeCodes?.includes('30') && b.planCoverage);
  return line?.planCoverage ?? null;
}

// A benefits check goes "stale" once the calendar month rolls over relative to when it last ran —
// i.e. it last completed before the 1st of the current month (OLIO-2686). `now` is injectable for tests.
export function benefitsCheckStale(action: WorkflowAction | undefined, now: Date = new Date()): boolean {
  const completed = parseDate(action?.completedAt);
  return completed != null && completed < startOfMonth(now);
}

export interface StcGroup {
  stcCode: string;
  stcName: string;
  lines: BenefitLine[];
}

// Group the raw 271 benefit lines by service-type code (STC), preserving first-seen order —
// this is the "STC 30 Health Benefit Plan Coverage / STC 35 Dental Care / …" grouping.
export function groupBenefitsByStc(benefits: BenefitLine[] | undefined): StcGroup[] {
  const groups = new Map<string, StcGroup>();
  (benefits ?? []).forEach((line) => {
    const codes = line.serviceTypeCodes?.length ? line.serviceTypeCodes : ['—'];
    const names = line.serviceTypes ?? [];
    codes.forEach((code, i) => {
      const existing = groups.get(code);
      if (existing) existing.lines.push(line);
      else groups.set(code, { stcCode: code, stcName: names[i] ?? names[0] ?? '', lines: [line] });
    });
  });
  return Array.from(groups.values());
}

// Split the STC groups for the curated benefits table: drop the skilled-nursing codes (they get
// their own section), then partition the rest into priority (shown expanded) and the long tail
// (collapsed behind a "show all" toggle).
export function partitionStcGroups(benefits: BenefitLine[] | undefined): { priority: StcGroup[]; rest: StcGroup[] } {
  const groups = groupBenefitsByStc(benefits).filter((g) => !SNF_STC_CODES.includes(g.stcCode));
  const priority = groups.filter((g) => PRIORITY_STC_CODES.includes(g.stcCode));
  const rest = groups.filter((g) => !PRIORITY_STC_CODES.includes(g.stcCode));
  return { priority, rest };
}

// ---- Skilled-nursing presentation ----

export const SNF_STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  non_covered: 'Not covered',
  inactive: 'Inactive',
  not_reported: 'Not reported',
};

export function snfStatusLabel(status?: string): string {
  return status ? (SNF_STATUS_LABEL[status] ?? status) : 'Not reported';
}

// A per-network SNF bucket → labeled detail rows (only the parts the payer actually reported).
export function snfBucketRows(bucket: SnfBucket | undefined): { label: string; value: string }[] {
  if (!bucket) return [];
  const rows: { label: string; value: string }[] = [{ label: 'Status', value: snfStatusLabel(bucket.status) }];
  const per = bucket.per ? ` / ${bucket.per.toLowerCase()}` : '';

  if (bucket.coinsurancePercent != null)
    rows.push({ label: 'Co-insurance', value: `${Math.round(bucket.coinsurancePercent * 100)}%${per}` });
  if (bucket.copay != null) rows.push({ label: 'Co-pay', value: `$${bucket.copay}${per}` });
  if (bucket.deductible != null) rows.push({ label: 'Deductible', value: `$${bucket.deductible}` });
  if (bucket.outOfPocket != null) rows.push({ label: 'Out of pocket', value: `$${bucket.outOfPocket}` });

  const dl = bucket.dayLimit;
  if (dl?.value != null)
    rows.push({ label: 'Day limit', value: `${dl.value} ${dl.unit ?? 'days'}${dl.period ? ` / ${dl.period}` : ''}` });

  const auth = bucket.priorAuth?.required;
  if (auth === 'yes') rows.push({ label: 'Prior auth', value: 'Required' });
  else if (auth === 'maybe') rows.push({ label: 'Prior auth', value: 'May be required' });
  else if (auth === 'no') rows.push({ label: 'Prior auth', value: 'Not required' });

  return rows;
}

// Does a bucket carry any reported signal (vs the payer staying silent)?
export function snfBucketReported(bucket?: SnfBucket): boolean {
  return !!bucket && !!bucket.status && bucket.status !== 'not_reported';
}
